import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useTri, EnteteTri, formatAnciennete, niveauAnciennete, useHorloge } from './TriTableau';
import './AccordClientImmoModal.css';

const STATUT_NOM = 'Accord client immo';

// Tableau de suivi des véhicules bloqués en attente d'accord client (statut
// atelier « Accord client immo »), sans notion de plage de dates — contrairement
// à la grille du planning, il doit remonter TOUTES les interventions dans ce
// statut, même anciennes, pour ne pas en perdre en cours de route.
export default function AccordClientImmoModal({ show, onClose, statutsAtelier = [], onOpenIntervention, inline = false }) {
  const [loading, setLoading] = useState(true);
  const [interventions, setInterventions] = useState([]);
  const [error, setError] = useState('');
  const tri = useTri(null, 'asc');
  const maintenant = useHorloge();

  const statut = statutsAtelier.find(s => s.nom === STATUT_NOM);
  const visible = inline || show; // en mode page, toujours chargé

  useEffect(() => {
    if (!visible) return;
    if (!statut) { setLoading(false); return; }
    setLoading(true);
    setError('');
    api.get('/planning/interventions', { params: { statut_atelier_id: statut.id } })
      .then(r => setInterventions(r.data.interventions || []))
      .catch(() => setError('Erreur de chargement.'))
      .finally(() => setLoading(false));
  }, [visible, statut]);

  if (!visible) return null;

  // La colonne « Depuis » lisait `updated_at`, qui bouge à la moindre
  // modification du dossier : corriger un commentaire remettait le compteur à
  // zéro. Elle lit désormais `statut_atelier_since`, posé par le déclencheur
  // uniquement quand le statut change — et au quart d'heure près plutôt qu'au
  // jour, un accord client attendu depuis 6 h n'étant pas « aujourd'hui ».
  const triees = tri.appliquer(interventions, {
    immat: i => i.immatriculation,
    client: i => i.client,
    vehicule: i => [i.marque, i.modele].filter(Boolean).join(' '),
    or: i => i.numero_or,
    concession: i => i.concession_nom,
    mecanicien: i => i.mecanicien,
    depuis: i => (i.statut_atelier_since ? Date.parse(i.statut_atelier_since) : null),
  });

  const content = (
    <>
      {!statut && (
        <div className="aci-empty">Statut « {STATUT_NOM} » introuvable dans les référentiels.</div>
      )}
      {statut && loading && <div className="aci-empty">Chargement…</div>}
      {statut && !loading && error && <div className="status-error">{error}</div>}
      {statut && !loading && !error && interventions.length === 0 && (
        <div className="aci-empty">Aucun véhicule en attente d'accord client (immobilisé) pour l'instant.</div>
      )}
      {statut && !loading && !error && interventions.length > 0 && (
        <div className="table-container aci-table-wrap">
          <table>
            <thead>
              <tr>
                <EnteteTri tri={tri} cle="immat">Immat</EnteteTri>
                <EnteteTri tri={tri} cle="client">Client</EnteteTri>
                <EnteteTri tri={tri} cle="vehicule">Véhicule</EnteteTri>
                <EnteteTri tri={tri} cle="or">N° OR</EnteteTri>
                <EnteteTri tri={tri} cle="concession">Concession</EnteteTri>
                <EnteteTri tri={tri} cle="mecanicien">Mécanicien</EnteteTri>
                <EnteteTri tri={tri} cle="depuis">Depuis</EnteteTri>
              </tr>
            </thead>
            <tbody>
              {triees.map(i => (
                <tr key={i.id} className="aci-row" onClick={() => onOpenIntervention(i)}>
                  <td>{i.immatriculation || '—'}</td>
                  <td>{i.client || '—'}</td>
                  <td>{[i.marque, i.modele].filter(Boolean).join(' ') || '—'}</td>
                  <td>{i.numero_or || '—'}</td>
                  <td>{i.concession_nom || '—'}</td>
                  <td>{i.mecanicien || '—'}</td>
                  <td>
                    {formatAnciennete(i.statut_atelier_since, maintenant)
                      ? <span
                          className={`tt-anciennete ${niveauAnciennete(i.statut_atelier_since, maintenant, { attention: 24, alerte: 72 })}${i.statut_atelier_since_estime ? ' tt-estime' : ''}`}
                          title={i.statut_atelier_since_estime
                            ? "Durée approchée : ce dossier était déjà dans ce statut avant la mise en service du suivi. Elle deviendra exacte au prochain changement de statut."
                            : new Date(i.statut_atelier_since).toLocaleString('fr-FR')}
                        >
                          {i.statut_atelier_since_estime ? '~ ' : ''}{formatAnciennete(i.statut_atelier_since, maintenant)}
                        </span>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  // Mode page intégrée (comme l'onglet Assistance technique)
  if (inline) {
    return (
      <div className="aci-page">
        <h2 className="aci-page-title">📋 Accord Client Immo</h2>
        {content}
      </div>
    );
  }

  // Mode modale (legacy — conservé pour compat)
  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📋 Accord Client Immo</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{content}</div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
